import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../styles/dashboard.css';
import '../styles/counselors.css';
import '../styles/students.css';
import '../styles/applications.css';
import '../styles/visa.css';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { ViewModeWrap } from '../components/ViewRecordModal';
import VisaEnquiryFormFields from '../components/VisaEnquiryFormFields';
import { emptyVisaEnquiryForm, mapVisaEnquiryFromApi, applicantDisplayName } from '../constants/visaEnquiryDefaults';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPassport, faSave, faTimes, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const API_URL = process.env.REACT_APP_API_URL || 'https://unilinkapi-production.up.railway.app';

export default function EditVisaEnquiry({ recordId, viewOnly = false } = {}) {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const id = recordId || paramId;
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(emptyVisaEnquiryForm());
  const [enquiryRef, setEnquiryRef] = useState('');
  const [submittedAt, setSubmittedAt] = useState('');
  const [countries, setCountries] = useState([]);
  const [creatingVisa, setCreatingVisa] = useState(false);
  const [showVisaModal, setShowVisaModal] = useState(false);
  const [visaModalError, setVisaModalError] = useState('');
  const [createdVisa, setCreatedVisa] = useState(null);

  useEffect(() => {
    fetchCountries();
    fetchEnquiry();
  }, [id]);

  const fetchCountries = async () => {
    try {
      const res = await fetch(`${API_URL}/countries`);
      const data = await res.json();
      if (data.success) setCountries(data.countries.filter((c) => c.status === 'Active'));
    } catch (e) { console.error(e); }
  };

  const fetchEnquiry = async () => {
    try {
      setFetching(true);
      const res = await fetch(`${API_URL}/visa-enquiries/${id}`);
      const data = await res.json();
      if (data.success) {
        setFormData(mapVisaEnquiryFromApi(data.enquiry));
        setEnquiryRef(data.enquiry.enquiry_id || '');
        setSubmittedAt(data.enquiry.created_at || '');
      } else {
        alert('Visa enquiry not found');
        if (!viewOnly) navigate('/visa-enquiries');
      }
    } catch (e) {
      alert('Failed to load visa enquiry');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openVisaModal = () => {
    setVisaModalError('');
    setCreatedVisa(null);
    setShowVisaModal(true);
  };

  const closeVisaModal = () => {
    if (creatingVisa) return;
    setShowVisaModal(false);
    setVisaModalError('');
    setCreatedVisa(null);
  };

  const handleAssignToVisa = async () => {
    setCreatingVisa(true);
    setVisaModalError('');
    try {
      const res = await fetch(`${API_URL}/visa-enquiries/${id}/create-visa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        setCreatedVisa({
          visaId: data.visaId,
          generatedVisaId: data.generatedVisaId,
          mapped: data.mapped || null,
        });
      } else {
        setVisaModalError(data.message || 'Failed to create visa');
      }
    } catch (err) {
      setVisaModalError('Network error. Please try again.');
    } finally {
      setCreatingVisa(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (viewOnly) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_URL}/visa-enquiries/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        alert('Visa enquiry updated successfully!');
        navigate('/visa-enquiries');
      } else {
        setError(data.message || 'Failed to update');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      setError('Network error. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (val) => {
    if (!val) return '-';
    return new Date(val).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const previewName = applicantDisplayName(formData);

  if (fetching) {
    return (
      <ViewModeWrap viewOnly={viewOnly}>
        <Sidebar />
        <div className="main-content" id="mainContent">
          <Header />
          <div style={{ textAlign: 'center', padding: '40px' }}><p>Loading visa enquiry...</p></div>
        </div>
      </ViewModeWrap>
    );
  }

  return (
    <ViewModeWrap viewOnly={viewOnly}>
      <Sidebar />
      <div className="main-content" id="mainContent">
        <Header />
        <div className="counselors-container visa-enquiry-edit">
          <div className="counselors-header">
            <div className="header-left">
              <h1>
                <FontAwesomeIcon icon={faPassport} className="page-icon" />
                {viewOnly ? 'Visa Enquiry Details' : 'Edit Visa Enquiry'}
              </h1>
            </div>
            {!viewOnly && (
              <div className="header-right">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={openVisaModal}
                  style={{ marginRight: '10px' }}
                >
                  <FontAwesomeIcon icon={faPassport} style={{ marginRight: '8px' }} />
                  Assign To Visa
                </button>
                <Link to="/visa-enquiries" className="btn-secondary">
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Back to Enquiries
                </Link>
              </div>
            )}
          </div>

          {error && (
            <div className="visa-enquiry-alert visa-enquiry-alert-error">
              {error}
            </div>
          )}

          {enquiryRef && (
            <div className="student-id-card">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="student-id-info">
                    <span className="id-label">Enquiry ID:</span>
                    <span className="student-id-display">{enquiryRef}</span>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <span className="visa-enquiry-submitted">
                    Submitted: {formatDate(submittedAt)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="counselor-form visa-enquiry-form">
            <VisaEnquiryFormFields
              variant="admin"
              data={formData}
              onChange={handleChange}
              disabled={viewOnly}
              countries={countries}
            />
            {!viewOnly && (
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => navigate('/visa-enquiries')} disabled={loading}>
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  <FontAwesomeIcon icon={faSave} /> {loading ? 'Saving...' : 'Update Enquiry'}
                </button>
              </div>
            )}
          </form>
        </div>

        {showVisaModal && (
          <>
            <div className="modal-backdrop show" onClick={closeVisaModal}></div>
            <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                  <div className="modal-header" style={{
                    background: 'linear-gradient(135deg, #F47B20 0%, #e8660f 100%)',
                    padding: '20px 28px', borderBottom: 'none'
                  }}>
                    <div>
                      <h5 className="modal-title" style={{ color: 'white', fontSize: '18px', fontWeight: '700', margin: 0 }}>
                        <FontAwesomeIcon icon={faPassport} style={{ marginRight: '10px' }} />
                        {createdVisa ? 'Visa Created' : 'Assign To Visa'}
                      </h5>
                      <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', margin: '4px 0 0' }}>
                        {createdVisa
                          ? 'Visa record is ready to edit'
                          : 'Create a visa from this enquiry'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-close-modal"
                      onClick={closeVisaModal}
                      disabled={creatingVisa}
                      style={{
                        color: 'white', background: 'rgba(255,255,255,0.2)', border: 'none',
                        borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>

                  <div className="modal-body" style={{ padding: '24px 28px' }}>
                    {visaModalError && (
                      <div style={{
                        background: '#fee2e2', color: '#991b1b', padding: '12px 16px',
                        borderRadius: '10px', marginBottom: '16px', border: '1px solid #fecaca',
                        fontSize: '13px'
                      }}>
                        {visaModalError}
                      </div>
                    )}

                    {createdVisa ? (
                      <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                        <div style={{
                          width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px',
                          background: '#dcfce7', color: '#16a34a', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', fontSize: '28px'
                        }}>
                          <FontAwesomeIcon icon={faPassport} />
                        </div>
                        <p style={{ fontSize: '15px', color: '#374151', marginBottom: '8px', lineHeight: 1.6 }}>
                          Visa <strong>{createdVisa.generatedVisaId}</strong> has been created successfully.
                        </p>
                        <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                          Name, email, phone, and country were copied. All other visa fields use defaults.
                        </p>
                      </div>
                    ) : (
                      <>
                        <p style={{ fontSize: '15px', color: '#374151', marginBottom: '16px', lineHeight: 1.6 }}>
                          Create a new visa for <strong>{previewName || 'this applicant'}</strong>?
                          Only name, email, phone, and country will be copied — other visa fields stay at defaults.
                        </p>
                        <div style={{
                          background: '#f9fafb', padding: '16px', borderRadius: '10px',
                          border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151'
                        }}>
                          {enquiryRef && (
                            <div style={{ marginBottom: '8px' }}><strong>Enquiry:</strong> {enquiryRef}</div>
                          )}
                          <div style={{ marginBottom: '8px' }}><strong>Name:</strong> {previewName || '-'}</div>
                          <div style={{ marginBottom: '8px' }}><strong>Email:</strong> {formData.email || '-'}</div>
                          <div style={{ marginBottom: '8px' }}><strong>Phone:</strong> {formData.mobileNo || '-'}</div>
                          <div><strong>Country:</strong> {formData.intendedCountry || '-'}</div>
                        </div>
                        <div style={{
                          marginTop: '16px', background: '#fff7ed', padding: '14px 16px',
                          borderRadius: '10px', border: '1px solid #fed7aa', fontSize: '13px', color: '#9a3412'
                        }}>
                          <FontAwesomeIcon icon={faPassport} style={{ marginRight: '8px', color: '#F47B20' }} />
                          Visa type defaults to <strong>Study Visa</strong> with status <strong>In Progress</strong>.
                        </div>
                      </>
                    )}
                  </div>

                  <div className="modal-footer" style={{
                    padding: '16px 28px', borderTop: '1px solid #f3f4f6',
                    display: 'flex', justifyContent: 'flex-end', gap: '10px'
                  }}>
                    {createdVisa ? (
                      <>
                        <button type="button" className="btn-secondary" onClick={closeVisaModal}>
                          Close
                        </button>
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => navigate(`/edit-visa/${createdVisa.visaId}`)}
                        >
                          <FontAwesomeIcon icon={faPassport} style={{ marginRight: '8px' }} />
                          Open Visa
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="btn-secondary" onClick={closeVisaModal} disabled={creatingVisa}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={handleAssignToVisa}
                          disabled={creatingVisa}
                        >
                          <FontAwesomeIcon icon={faPassport} style={{ marginRight: '8px' }} />
                          {creatingVisa ? 'Creating...' : 'Confirm & Create Visa'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </ViewModeWrap>
  );
}
